// Global single-date input mask.
//
// element-plus <el-date-picker> is a fragment-root component (its calendar panel
// teleports), so a Vue custom directive placed on it does NOT function. Instead we
// attach one document-level capture-phase "input" listener that masks the text of
// any *single-date* picker input (class `el-date-editor--date`): non-digits are
// stripped, separators are auto-inserted per the picker's own format, month/day
// segments are validated (so an impossible value like month "31" can't be typed and
// silently roll over into a wrong date), and typing stops once the date is full.
//
// The mask template is read from each picker's own `format` prop, so a picker using
// MM-DD-YYYY, MM/DD/YYYY or YYYY-MM-DD is each masked correctly. Range and datetime
// pickers are intentionally left untouched.

const FALLBACK_TEMPLATE = "MM-DD-YYYY";
const isLetter = (c) => /[A-Za-z]/.test(c);
const hasTimeToken = (t) => /[hHmsAa]/.test(t);

// Split a format into ordered segments: digit runs (MM / DD / YYYY) and separators.
const segmentsOf = (template) => {
  const segs = [];
  let i = 0;
  while (i < template.length) {
    const c = template[i];
    if (isLetter(c)) {
      let j = i;
      while (j < template.length && template[j] === c) j++;
      segs.push({ digit: true, letter: c.toUpperCase(), len: j - i });
      i = j;
    } else {
      segs.push({ digit: false, char: c });
      i++;
    }
  }
  return segs;
};

// Build the masked value from raw digits, validating month (01-12) and day (01-31).
const maskDate = (rawDigits, template) => {
  const segs = segmentsOf(template);
  let out = "";
  let di = 0;
  for (const seg of segs) {
    if (!seg.digit) {
      if (di < rawDigits.length) out += seg.char;
      continue;
    }
    let val = "";
    let k = 0;
    while (k < seg.len && di < rawDigits.length) {
      const d = rawDigits[di];
      const isMonth = seg.letter === "M";
      const isDay = seg.letter === "D";

      // First digit of month/day: if it can't be a valid tens digit, auto-pad a
      // leading zero (e.g. "3" -> "03") and finish the segment.
      if ((isMonth || isDay) && k === 0) {
        const maxTens = isMonth ? "1" : "3";
        if (d > maxTens) {
          val = "0" + d;
          di++;
          break;
        }
      }
      // Second digit of month/day: discard it if it would exceed the valid range.
      if ((isMonth || isDay) && k === 1) {
        const combined = parseInt(val + d, 10);
        const limit = isMonth ? 12 : 31;
        if (combined === 0 || combined > limit) {
          di++; // drop the invalid digit, wait for a valid one
          continue;
        }
      }

      val += d;
      di++;
      k++;
    }
    out += val;
  }
  return out;
};

const countDigits = (template) => template.split("").filter(isLetter).length;

// Read the picker's `format` prop off the Vue component instance attached to the DOM.
const resolveTemplate = (editor) => {
  try {
    let inst = editor.__vueParentComponent;
    for (let i = 0; inst && i < 6; i++) {
      const fmt = inst.props && inst.props.format;
      if (typeof fmt === "string" && fmt && !hasTimeToken(fmt)) {
        return fmt;
      }
      inst = inst.parent;
    }
  } catch (e) {
    /* fall through to default */
  }
  return FALLBACK_TEMPLATE;
};

export function installDateMask() {
  document.addEventListener(
    "input",
    (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement)) return;

      // Only plain single-date pickers.
      const editor = input.closest(".el-date-editor");
      if (
        !editor ||
        editor.classList.contains("el-range-editor") ||
        !editor.classList.contains("el-date-editor--date")
      ) {
        return;
      }

      const template = resolveTemplate(editor);
      const digits = input.value.replace(/\D/g, "").slice(0, countDigits(template));
      const masked = maskDate(digits, template);
      if (masked !== input.value) {
        input.value = masked;
        // Re-emit so element-plus reads the corrected value. Guarded: once
        // masked === input.value this handler makes no further change.
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    },
    true // capture: run before element-plus's own bubble-phase handler
  );
}

export default installDateMask;
